import React, { useCallback, useEffect, useMemo, useState } from "react";
import BlockUi from "react-block-ui";
import DataTable from "react-data-table-component";
import ApiKeyReveal from "../components/ApiKeyReveal";
import CalculatorApiTabs from "../components/CalculatorApiTabs";
import { JdsPagination } from "../../../components/jds/JdsPagination";
import { JdsSearchInput } from "../../../components/jds/JdsSearchInput";
import { jdsTableStyles } from "../../../components/jds/tableStyles";
import { JdsTableSkeleton } from "../../../components/jds/JdsSkeleton";
import "../../../components/jds/jds.css";
import "./vinculacao.css";
import { describeApiError } from "../utils/describeApiError";
import {
  createClient,
  getClient,
  listClients,
  rotateApiKey,
  updateClientScopes,
} from "../services";
import ClientScopesModal from "./ClientScopesModal";
import { buildClientColumns } from "./columns";
import JusfyClientsTable from "./JusfyClientsTable";

const SERVICE_UNAVAILABLE_MESSAGE =
  "Serviço de clientes indisponível. Tente novamente mais tarde.";

const SCOPES_MODAL_MODE = { create: "create", edit: "edit" };

const ROTATE_CONFIRMATION = clientName =>
  `Rotacionar a API key de ${clientName}?\n\n` +
  "A key atual PARA DE FUNCIONAR IMEDIATAMENTE — a integração do cliente cai " +
  "até ele trocar o segredo do lado dele. Não há período de graça.\n\n" +
  "Se ele apenas perdeu a key, cancele e use \"Ver API key\" para reexibir a " +
  "mesma.";

export function CalculatorApiClients() {
  const [clients, setClients] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [listError, setListError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [loading, setLoading] = useState(false);

  // null | { mode: create, jusfyClientId, name } | { mode: edit, client }
  const [scopesModal, setScopesModal] = useState(null);
  const [scopesModalError, setScopesModalError] = useState(null);
  const [savingScopes, setSavingScopes] = useState(false);

  // A credencial vive SÓ aqui, em estado local e efêmero: sai ao desmontar, e
  // nunca entra em Redux — redux-persist a gravaria em localStorage.
  const [revealedClient, setRevealedClient] = useState(null);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      setClients(await listClients());
      setListError(null);
    } catch (error) {
      setListError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Qual cliente da Jusfy já tem cliente no Calculator API. `backendClientId`
  // null fica de fora: é cliente interno do calculator-api, sem contraparte na
  // Jusfy — a lista deixou de filtrar por vínculo e agora traz os dois tipos.
  //
  // Chaveado por string porque os dois lados são APIs diferentes: o vínculo vem
  // number do calculator-api e o id da listagem da Jusfy pode vir string. Com
  // Map por valor cru, 42 !== "42" e todo mundo apareceria como não vinculado.
  const linkByJusfyClientId = useMemo(
    () =>
      new Map(
        clients
          .filter(client => client.backendClientId !== null)
          .map(client => [String(client.backendClientId), client]),
      ),
    [clients],
  );

  // O ÚNICO caminho de provisionamento (RF-21): o nome e o vínculo saem da linha
  // clicada, então não há id digitado à mão para errar — é por isso que não
  // existe form de criação nesta tela. Mandar o vínculo já na criação é também o
  // que torna a operação reconciliável se a rede cair — sem ele, nome duplicado
  // é aceito e não dá para saber o que foi criado.
  //
  // Nome e vínculo errados na criação só se corrigem pela v3: a edição desta
  // tela mexe só nos produtos.
  //
  // `jusfyClientId` chega pronto da tabela: é o id do CLIENTE, não de usuário, e
  // já vem number (o contrato recusa string com 422).
  //
  // O modal de produtos é também a confirmação, a única barreira antes de um
  // vínculo IRREVERSÍVEL: o calculator-api não valida o `backendClientId` contra
  // a nossa base (aceita qualquer inteiro positivo). Por isso ele nomeia o
  // cliente e o id — é onde o admin confere.
  const openScopesModal = modal => {
    setActionError(null);
    setScopesModalError(null);
    setScopesModal(modal);
  };

  const createLinkFor = ({ jusfyClientId, name }) =>
    openScopesModal({ mode: SCOPES_MODAL_MODE.create, jusfyClientId, name });

  const editScopesOf = client =>
    openScopesModal({ mode: SCOPES_MODAL_MODE.edit, client });

  // O erro fica no modal, com a seleção intacta: o 400 de escopo inválido lista
  // os escopos recusados, e o admin corrige ali mesmo.
  //
  // Na criação, 502 não diz se o cliente foi criado. NÃO repetir
  // automaticamente: a rota não é idempotente. Para reconciliar, procure o
  // cliente na lista — o vínculo é único, é o que gera o 409.
  const saveScopes = async scopes => {
    setScopesModalError(null);
    setSavingScopes(true);
    try {
      if (scopesModal.mode === SCOPES_MODAL_MODE.create) {
        const { jusfyClientId, name } = scopesModal;
        setRevealedClient(await createClient({ name, jusfyClientId, scopes }));
      } else {
        await updateClientScopes(scopesModal.client.id, scopes);
      }
      setScopesModal(null);
      await loadClients();
    } catch (error) {
      setScopesModalError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setSavingScopes(false);
    }
  };

  const showApiKey = async client => {
    setActionError(null);
    setLoading(true);
    try {
      setRevealedClient(await getClient(client.id));
    } catch (error) {
      setActionError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setLoading(false);
    }
  };

  const rotate = async client => {
    if (!window.confirm(ROTATE_CONFIRMATION(client.name))) return;

    setActionError(null);
    setLoading(true);
    try {
      setRevealedClient(await rotateApiKey(client.id));
      await loadClients();
    } catch (error) {
      setActionError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setLoading(false);
    }
  };

  const columns = buildClientColumns({
    onShowApiKey: showApiKey,
    onRotate: rotate,
    onEditScopes: editScopesOf,
  });

  // A rota de clientes do calculator-api não pagina e devolve a lista inteira,
  // então busca e paginação desta tabela são do front mesmo.
  const filteredClients = useMemo(() => {
    const term = nameFilter.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(client => client.name.toLowerCase().includes(term));
  }, [clients, nameFilter]);

  return (
    <div className="jds-scope">
      <CalculatorApiTabs />

      <BlockUi tag="div" blocking={loading}>
        {actionError && (
          <div className="vinculacao-alert vinculacao-alert--warning" role="alert">
            {actionError}
          </div>
        )}

        {scopesModal?.mode === SCOPES_MODAL_MODE.create && (
          <ClientScopesModal
            title="Criar vínculo"
            description={
              <p>
                Cliente do Calculator API para{" "}
                <strong>{scopesModal.name}</strong> (cliente{" "}
                {scopesModal.jusfyClientId} da Jusfy). A API key é emitida na
                hora e já autentica: repasse por canal seguro. Marque os
                produtos que ele pode usar.
              </p>
            }
            initialScopes={[]}
            confirmLabel="Criar cliente"
            saving={savingScopes}
            errorMessage={scopesModalError}
            onConfirm={saveScopes}
            onHide={() => setScopesModal(null)}
          />
        )}

        {scopesModal?.mode === SCOPES_MODAL_MODE.edit && (
          <ClientScopesModal
            title={`Produtos de ${scopesModal.client.name}`}
            description={
              <p>
                A lista salva substitui a atual. Produto desmarcado deixa de
                funcionar na requisição seguinte.
              </p>
            }
            initialScopes={scopesModal.client.scopes ?? []}
            confirmLabel="Salvar produtos"
            saving={savingScopes}
            errorMessage={scopesModalError}
            onConfirm={saveScopes}
            onHide={() => setScopesModal(null)}
          />
        )}

        {revealedClient && (
          <ApiKeyReveal
            clientName={revealedClient.name}
            apiKey={revealedClient.apiKey}
          />
        )}

        {/* O único caminho de criação da tela: todo cliente nasce vinculado, a
            partir da linha clicada — não há form onde digitar nome ou id. */}
        <section className="jds-card jds-card--flush vinculacao-section">
          <div className="vinculacao-section__header">
            <h2 className="jds-title-md">Clientes da Jusfy</h2>
            <p className="text-muted">
              Ache o cliente, clique em <strong>Criar vínculo</strong> e escolha
              os produtos: o cliente é criado no Calculator API já vinculado, e
              a API key é emitida na hora — ela aparece aqui em cima, mascarada.
              O nome sai da linha e não pode ser corrigido depois.
            </p>
          </div>

          <div className="vinculacao-section__table">
            <JusfyClientsTable
              linkByJusfyClientId={linkByJusfyClientId}
              onCreateLink={createLinkFor}
            />
          </div>
        </section>

        <section className="jds-card jds-card--flush vinculacao-section">
          <div className="vinculacao-section__header">
            <h2 className="jds-title-md">Clientes do Calculator API</h2>
            <p className="text-muted">
              Inclui os clientes internos, que não têm contraparte na Jusfy. Não
              há exclusão nem inativação por esta tela: para fechar o acesso de
              uma key comprometida, rotacione — o histórico de consumo é
              preservado.
            </p>
          </div>

          {listError ? (
            <div className="vinculacao-section__body">
              <div
                className="vinculacao-alert vinculacao-alert--warning"
                role="alert"
              >
                {listError}
              </div>
            </div>
          ) : (
            <div className="vinculacao-section__table">
              <DataTable
                columns={columns}
                data={filteredClients}
                customStyles={jdsTableStyles}
                progressPending={loading}
                progressComponent={<JdsTableSkeleton />}
                pagination
                paginationComponent={JdsPagination}
                responsive={false}
                style={{ width: "100%" }}
                noDataComponent="Nenhum cliente cadastrado."
                subHeader
                subHeaderAlign="left"
                subHeaderComponent={
                  <JdsSearchInput
                    id="vinculacao-clients-filter-name"
                    placeholder="Buscar por nome"
                    filterText={nameFilter}
                    onFilter={event => setNameFilter(event.target.value)}
                    onClear={() => setNameFilter("")}
                  />
                }
              />
            </div>
          )}
        </section>
      </BlockUi>
    </div>
  );
}
